/**
 * Logger utility for consistent logging across the application
 * Usage: import logger from '../utils/logger';
 * Then: logger.info('Message'); logger.error('Error message', error);
 */

import ENV from '../config/environments';

class Logger {
  /**
   * Log an informational message
   * @param {string} message - The message to log
   * @param {object} data - Optional data to include
   */
  info(message, data = null) {
    if (!ENV.ENABLE_LOGS) return;
    
    if (data) {
      console.log(`[INFO] ${message}`, data);
    } else {
      console.log(`[INFO] ${message}`);
    }
  }
  
  /**
   * Log a warning message
   * @param {string} message - The message to log
   * @param {object} data - Optional data to include
   */
  warn(message, data = null) {
    if (!ENV.ENABLE_LOGS) return;
    
    if (data) {
      console.warn(`[WARN] ${message}`, data);
    } else {
      console.warn(`[WARN] ${message}`);
    }
  }
  
  /**
   * Log an error message
   * @param {string} message - The error message
   * @param {Error} error - The error object
   */
  error(message, error = null) {
    // Always log errors, even in production
    if (error) {
      console.error(`[ERROR] ${message}`, error);
      
      // In development, also log the stack trace
      if (ENV.ENABLE_LOGS && error.stack) {
        console.error(error.stack);
      }
    } else {
      console.error(`[ERROR] ${message}`);
    }
  }
  
  /**
   * Log a debug message (only in development)
   * @param {string} message - The message to log
   * @param {object} data - Optional data to include
   */
  debug(message, data = null) {
    if (!ENV.ENABLE_LOGS) return;
    
    if (data) {
      console.debug(`[DEBUG] ${message}`, data);
    } else {
      console.debug(`[DEBUG] ${message}`);
    }
  }
  
  /**
   * Log API request details
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @param {object} data - Request data
   */
  apiRequest(method, url, data = null) {
    if (!ENV.ENABLE_LOGS) return;
    
    if (data) {
      console.log(`[API] ${method} ${url}`, data);
    } else {
      console.log(`[API] ${method} ${url}`);
    }
  }
  
  /**
   * Log API response details
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @param {object} response - Response data
   * @param {number} status - HTTP status code
   */
  apiResponse(method, url, response, status) {
    if (!ENV.ENABLE_LOGS) return;
    
    console.log(`[API] ${method} ${url} - Status: ${status}`, response);
  }
}

// Create and export a singleton instance
const logger = new Logger();
export default logger;